import * as React from "react";
import { cn } from "@/lib/cn";
import { useTheme, type ThemeMode } from "@/app/theme";

const LABELS: Record<ThemeMode, string> = {
    system: "Auto",
    light: "Light",
    dark: "Dark",
};

const ICONS: Record<ThemeMode, string> = {
    system: "◐",
    light: "☀",
    dark: "☾",
};

const NEXT: Record<ThemeMode, ThemeMode> = {
    system: "light",
    light: "dark",
    dark: "system",
};

export interface ThemeToggleProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    /** Show only the icon (default false — icon + text). */
    iconOnly?: boolean;
}

/**
 * 3-state theme cycle button: Auto → Light → Dark → Auto.
 *
 * Reads from `useTheme()` and updates the `data-theme` attribute on
 * `<html>` synchronously. Persists to `localStorage["omni:theme"]`.
 */
export const ThemeToggle = React.forwardRef<HTMLButtonElement, ThemeToggleProps>(
    function ThemeToggle({ className, iconOnly = false, ...rest }, ref) {
        const { mode, cycle } = useTheme();
        const next = NEXT[mode];
        return (
            <button
                ref={ref}
                type="button"
                onClick={cycle}
                aria-label={`Theme: ${LABELS[mode]}. Switch to ${LABELS[next]}.`}
                title={`Theme: ${LABELS[mode]}`}
                className={cn(
                    "inline-flex items-center gap-2 rounded-md border border-border bg-bg-elevated px-3 h-8 text-xs",
                    "text-fg hover:border-border-strong transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus",
                    className,
                )}
                {...rest}
            >
                <span aria-hidden className="text-sm leading-none">{ICONS[mode]}</span>
                {!iconOnly && <span>{LABELS[mode]}</span>}
            </button>
        );
    },
);
